import React, { useEffect, useState } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { SxStyle } from '../../../types';
import firebaseApp from '../../../firebase-app';
import { doc, getDoc } from 'firebase/firestore';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { getMatchPath } from '../../../utils/paths';
import { Switch, Link, Route, useRouteMatch } from 'react-router-dom';
import MatchEvents from './match-events';
import MatchDetails from './match-details';
import MatchPushNotifications from './match-push-notifications';
import { Match } from './utils/data';
import Loading from '../../loading';
import moment from 'moment';
import { Divider, Stack } from '@mui/material';
import MatchStats from './match-stats';
import MatchLineup from './match-lineup';
import EditPlayerConfigModal from './components/edit-player-config-modal';
import ScenePage from '../../common/scene/scene-page';

const styles: SxStyle = {
  container: {
    width: '100%',
    marginBottom: 4,
  },
  header: (theme) => ({
    padding: theme.spacing(4),
  }),
  headerInfo: (theme) => ({
    color: theme.palette.grey[600],
  }),
  tabs: (theme) => ({
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
  }),
};

enum TABS {
  DETAILS = 'details',
  HOME_LINEUP = 'home-lineup',
  VISITING_LINEUP = 'visiting-lineup',
  STATS = 'stats',
  EVENTS = 'events',
  PUSH_NOTIFICATIONS = 'push-notifications',
}

interface MatchParams {
  leagueAbbrv: string;
  season: string;
  matchId: string;
  tab?: TABS;
}

export default function MatchInfo() {
  const { strings } = useStringsContext();
  const { url, path, params } = useRouteMatch<MatchParams>();
  const match = useRouteMatch<MatchParams>(`${path}/:tab?`);
  const [matchDoc, setMatchDoc] = useState<Match | undefined>();
  const [editMatchConfigPlayerId, setEditMatchConfigPlayerId] = useState<string | undefined>();
  const { leagueAbbrv, season, matchId } = params;
  const tab = match?.params.tab;

  const matchPath = getMatchPath(leagueAbbrv, season, matchId);

  useEffect(() => {
    const getMatchDoc = async () => {
      const docRef = doc(firebaseApp.db, matchPath);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setMatchDoc(docSnap.data() as Match);
      }
    };
    getMatchDoc();
  }, [matchPath]);

  if (!matchDoc) {
    return <Loading />;
  }

  const breadCrumps = [matchDoc.leagueName, matchDoc.configSeasonStartYear.toString()];

  return (
    <ScenePage title={strings.appBar.matches} breadCrumps={breadCrumps}>
      <Paper elevation={2} sx={styles.container}>
        <Box sx={styles.header}>
          <Typography variant="h4" noWrap>
            {matchDoc.homeTeamName} - {matchDoc.visitingTeamName}
          </Typography>
          <Box sx={styles.headerInfo}>
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
            >
              <div>Omgång {matchDoc.round}</div>
              <div>{moment(matchDoc.startDate).format('YYYY-MM-DD')}</div>
            </Stack>
          </Box>
        </Box>
        <Tabs
          value={tab || TABS.DETAILS}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          sx={styles.tabs}
        >
          <Tab value={TABS.DETAILS} component={Link} to={url} label={strings.appBar.matchDetails} />
          <Tab
            value={TABS.HOME_LINEUP}
            component={Link}
            to={`${url}/${TABS.HOME_LINEUP}`}
            label={`${strings.appBar.matchLineup} ${matchDoc.homeTeamName}`}
          />
          <Tab
            value={TABS.VISITING_LINEUP}
            component={Link}
            to={`${url}/${TABS.VISITING_LINEUP}`}
            label={`${strings.appBar.matchLineup} ${matchDoc.visitingTeamName}`}
          />
          <Tab
            value={TABS.STATS}
            component={Link}
            to={`${url}/${TABS.STATS}`}
            label={strings.appBar.matchStats}
          />
          <Tab
            value={TABS.EVENTS}
            component={Link}
            to={`${url}/${TABS.EVENTS}`}
            label={strings.appBar.matchEvents}
          />
          <Tab
            value={TABS.PUSH_NOTIFICATIONS}
            component={Link}
            to={`${url}/${TABS.PUSH_NOTIFICATIONS}`}
            label={strings.appBar.pushNotificationLog}
          />
        </Tabs>
        {matchDoc && (
          <Switch>
            <Route exact path={url}>
              <MatchDetails matchDoc={matchDoc} />
            </Route>
            <Route path={`${url}/${TABS.HOME_LINEUP}`}>
              <MatchLineup
                leagueAbbrv={leagueAbbrv}
                season={season}
                matchId={matchId}
                teamLineup={matchDoc.homeTeamLineup}
                onEditPlayer={(playerId) => setEditMatchConfigPlayerId(playerId)}
              />
            </Route>
            <Route path={`${url}/${TABS.VISITING_LINEUP}`}>
              <MatchLineup
                leagueAbbrv={leagueAbbrv}
                season={season}
                matchId={matchId}
                teamLineup={matchDoc.visitingTeamLineup}
                onEditPlayer={(playerId) => setEditMatchConfigPlayerId(playerId)}
              />
            </Route>
            <Route path={`${url}/${TABS.STATS}`}>
              <MatchStats matchDoc={matchDoc} />
            </Route>
            <Route path={`${url}/${TABS.EVENTS}`}>
              <MatchEvents matchDoc={matchDoc} />
            </Route>
            <Route path={`${url}/${TABS.PUSH_NOTIFICATIONS}`}>
              <MatchPushNotifications matchDoc={matchDoc} />
            </Route>
          </Switch>
        )}
        {editMatchConfigPlayerId && (
          <EditPlayerConfigModal
            onDone={() => {}}
            matchId={Number(matchId)}
            playerId={editMatchConfigPlayerId}
            open
            onClose={() => setEditMatchConfigPlayerId(undefined)}
            leagueAbbrv={leagueAbbrv}
            season={season}
          />
        )}
      </Paper>
    </ScenePage>
  );
}
